// web/src/features/landing/LandingPage.tsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Card,
  CardContent,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import {
  Restaurant as RestaurantIcon,
  ShoppingCart as ShoppingCartIcon,
  FamilyRestroom as FamilyIcon,
  Timer as TimerIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';

function LandingPage() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!email || !name) {
      setError('Please enter your name and email');
      return;
    }

    if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      // Send to backend waitlist API
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to join waitlist');
      }

      // Success!
      setSubmitted(true);
    } catch (err: any) {
      console.error('Waitlist signup error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <Box sx={{ backgroundColor: '#FAF5EA', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box sx={{ pt: { xs: 4, md: 8 }, pb: { xs: 6, md: 10 }, textAlign: 'center' }}>
          {/* Logo */}
          <Box sx={{ mb: 4 }}>
            <img
              src={logo}
              alt="Snack'd Logo"
              style={{
                maxHeight: '200px',
                maxWidth: '100%',
                height: 'auto'
              }}
            />
          </Box>

          {/* Launching Soon Badge */}
          <Box
            sx={{
              display: 'inline-block',
              backgroundColor: '#F78764',
              color: 'white',
              px: 3,
              py: 1,
              borderRadius: 20,
              mb: 3,
              fontWeight: 600,
              fontSize: '0.9rem',
              textTransform: 'uppercase',
              letterSpacing: 1,
            }}
          >
            🚀 Launching Soon
          </Box>

          {/* Headline */}
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
              mb: 2,
              color: '#2c3e50',
              lineHeight: 1.2,
            }}
          >
            AI-Powered Meal Planning<br />for Busy Families
          </Typography>

          {/* Subheadline */}
          <Typography
            variant="h5"
            sx={{
              fontSize: { xs: '1.1rem', sm: '1.3rem', md: '1.5rem' },
              mb: 5,
              color: '#34495e',
              fontWeight: 400,
              maxWidth: '800px',
              mx: 'auto',
              lineHeight: 1.5,
            }}
          >
            Personalized weekly meal plans, smart shopping lists, and step-by-step recipes—all tailored to your family's tastes and dietary needs.
          </Typography>

          {/* Email Capture Form */}
          {!submitted ? (
            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                maxWidth: '500px',
                mx: 'auto',
                mb: 3,
                backgroundColor: 'white',
                p: 4,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              }}
            >
              <Typography
                variant="h6"
                sx={{ mb: 2, fontWeight: 600, color: '#2c3e50' }}
              >
                Join the Waitlist
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 3, color: '#7f8c8d' }}
              >
                Get early access and be the first to know when we launch!
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Your Name"
                variant="outlined"
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 3 }}
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  backgroundColor: '#F78764',
                  '&:hover': {
                    backgroundColor: '#ff5252',
                  },
                }}
              >
                Get Early Access
              </Button>
              <Typography
                variant="caption"
                sx={{ display: 'block', mt: 2, color: '#95a5a6' }}
              >
                We respect your privacy. Unsubscribe at any time.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                maxWidth: '500px',
                mx: 'auto',
                mb: 3,
                backgroundColor: 'white',
                p: 4,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              }}
            >
              <CheckIcon sx={{ fontSize: 60, color: '#27ae60', mb: 2 }} />
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#2c3e50' }}>
                You're on the list! 🎉
              </Typography>
              <Typography variant="body1" sx={{ color: '#7f8c8d' }}>
                Thanks for joining, {name}! We'll send you an email at <strong>{email}</strong> when we launch.
              </Typography>
            </Box>
          )}

          {/* Already have account */}
          <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
            Already have an account?{' '}
            <MuiLink component={Link} to="/login" sx={{ fontWeight: 600 }}>
              Sign in
            </MuiLink>
          </Typography>
        </Box>
      </Container>

      {/* Features Section */}
      <Box sx={{ backgroundColor: 'white', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 2,
              color: '#2c3e50',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Everything You Need to Cook at Home
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 6,
              color: '#7f8c8d',
              fontWeight: 400,
            }}
          >
            Spend less time planning and more time enjoying delicious home-cooked meals
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              },
              gap: 4,
            }}
          >
            {/* Feature 1 */}
            <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <RestaurantIcon sx={{ fontSize: 50, color: '#F78764', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    AI-Powered Meal Plans
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                    Get personalized weekly meal plans based on your family's preferences and dietary needs
                  </Typography>
                </CardContent>
              </Card>

            {/* Feature 2 */}
            <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <ShoppingCartIcon sx={{ fontSize: 50, color: '#3498db', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Smart Shopping Lists
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                    Automatically generated shopping lists save you time and reduce food waste
                  </Typography>
                </CardContent>
              </Card>

            {/* Feature 3 */}
            <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <FamilyIcon sx={{ fontSize: 50, color: '#9b59b6', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Family-Friendly
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                    Accommodates mixed dietary needs—vegetarian and omnivore meals in one plan
                  </Typography>
                </CardContent>
              </Card>

            {/* Feature 4 */}
            <Card
                sx={{
                  height: '100%',
                  textAlign: 'center',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-5px)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <TimerIcon sx={{ fontSize: 50, color: '#f39c12', mb: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                    Quick & Easy Recipes
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#7f8c8d' }}>
                    Step-by-step recipes designed to fit your schedule and skill level
                  </Typography>
                </CardContent>
              </Card>
          </Box>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ backgroundColor: '#FAF5EA', py: { xs: 6, md: 10 } }}>
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 6,
              color: '#2c3e50',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Why Families Love Snack'd
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 3,
            }}
          >
            {[
              'Save hours every week on meal planning and grocery shopping',
              'Reduce food waste with precise ingredient lists',
              'Accommodate everyone\'s dietary preferences in one meal plan',
              'Discover new recipes your family will actually eat',
              'Track completed meals and build healthy cooking habits',
              'Set a weekly budget and stick to it',
            ].map((benefit, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckIcon sx={{ color: '#27ae60', mr: 2, fontSize: 28 }} />
                <Typography variant="body1" sx={{ color: '#2c3e50' }}>
                  {benefit}
                </Typography>
              </Box>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          backgroundColor: '#FAF5EA',
          py: { xs: 6, md: 8 },
          borderTop: '3px solid #F78764',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              mb: 2,
              color: '#2c3e50',
              fontSize: { xs: '1.8rem', md: '2.5rem' },
            }}
          >
            Ready to Transform Your Meal Planning?
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: 'center',
              mb: 4,
              color: '#34495e',
              fontWeight: 400,
            }}
          >
            Join the waitlist today and be among the first to experience stress-free meal planning
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              sx={{
                py: 2,
                px: 5,
                fontSize: '1.2rem',
                fontWeight: 600,
                textTransform: 'none',
                backgroundColor: '#F78764',
                color: 'white',
                boxShadow: '0 4px 12px rgba(255,107,107,0.3)',
                '&:hover': {
                  backgroundColor: '#ff5252',
                  boxShadow: '0 6px 16px rgba(255,107,107,0.4)',
                },
              }}
            >
              Join the Waitlist
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ backgroundColor: '#2c3e50', py: 4 }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 2,
              color: 'white',
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                © 2025 Snack'd. All rights reserved.
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Making home-cooked meals easy, healthy, and fun.
              </Typography>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', md: 'center' } }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                Legal
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'center' } }}>
                <MuiLink component={Link} to="/terms" sx={{ color: 'white', '&:hover': { color: '#F78764' } }}>
                  Terms of Service
                </MuiLink>
                <MuiLink component={Link} to="/privacy" sx={{ color: 'white', '&:hover': { color: '#F78764' } }}>
                  Privacy Policy
                </MuiLink>
              </Box>
            </Box>
            <Box sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Follow us on social media
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                <MuiLink href="#" sx={{ color: 'white', '&:hover': { color: '#F78764' } }}>
                  Instagram
                </MuiLink>
                <MuiLink href="#" sx={{ color: 'white', '&:hover': { color: '#F78764' } }}>
                  Facebook
                </MuiLink>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
